import React, { useContext, useEffect, useState } from 'react';
import MediaUploader from './MediaUploader';
import { US_STATES, isValidState } from '../constants/states';
import {
  isValidLicensePlate,
  sanitizeLicensePlateInput,
  PLATE_MAX_LENGTH,
  REVIEW_TITLE_MAX_LENGTH,
  REVIEW_TEXT_MAX_LENGTH,
  sanitizeTextInput,
  hasMaliciousPattern,
  hasBlockedPlateChars
} from '../utils/validation';
import { AuthContext } from '../contexts/AuthContext';

function ReviewModal({ onSubmit, onClose, submitting, uploadProgress = 0 }) {
  const { user } = useContext(AuthContext);
  const [plate, setPlate] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [plateInputError, setPlateInputError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const titleCount = title.length;
  const textCount = text.length;
  const titleCounterClass = titleCount >= REVIEW_TITLE_MAX_LENGTH - 10 ? 'text-rose-600' : 'text-slate-500';
  const textCounterClass = textCount >= 490 ? 'text-rose-600' : textCount >= 450 ? 'text-amber-600' : 'text-slate-500';

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;

    const timer = setInterval(() => {
      setCooldownSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handlePlateChange = (event) => {
    const rawValue = event.target.value || '';
    const normalizedValue = sanitizeLicensePlateInput(rawValue);
    const hasInvalidChars = /[^a-zA-Z0-9-]/.test(rawValue) || hasBlockedPlateChars(rawValue);

    setPlate(normalizedValue);
    setPlateInputError(hasInvalidChars ? 'Only letters, numbers, and hyphens are allowed' : '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const trimmedPlate = sanitizeTextInput(plate, PLATE_MAX_LENGTH).toUpperCase();
    const trimmedState = stateCode.trim().toUpperCase();
    const trimmedText = sanitizeTextInput(text, REVIEW_TEXT_MAX_LENGTH);
    const trimmedTitle = sanitizeTextInput(title, REVIEW_TITLE_MAX_LENGTH);

    if (plateInputError) {
      setError(plateInputError);
      return;
    }

    if (!trimmedPlate) {
      setError('License plate is required');
      return;
    }

    if (!isValidLicensePlate(trimmedPlate)) {
      setError('License plate must be 1-10 characters using letters, numbers, or hyphens');
      return;
    }

    if (hasMaliciousPattern(trimmedPlate) || hasMaliciousPattern(trimmedTitle) || hasMaliciousPattern(trimmedText)) {
      setError('Input rejected due to unsafe content');
      return;
    }

    if (!trimmedState) {
      setError('State is required');
      return;
    }

    if (!isValidState(trimmedState)) {
      setError('Invalid state');
      return;
    }

    if (!trimmedText) {
      setError('Description is required');
      return;
    }

    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }

    if (trimmedTitle.length > REVIEW_TITLE_MAX_LENGTH) {
      setError(`Title cannot exceed ${REVIEW_TITLE_MAX_LENGTH} characters`);
      return;
    }

    if (trimmedText.length > REVIEW_TEXT_MAX_LENGTH) {
      setError(`Description cannot exceed ${REVIEW_TEXT_MAX_LENGTH} characters`);
      return;
    }

    const formData = new FormData();
    formData.append('plate', trimmedPlate);
    formData.append('state', trimmedState);
    formData.append('title', trimmedTitle);
    formData.append('text', trimmedText);
    files.forEach(({ file }) => formData.append('media', file));

    try {
      await onSubmit(formData);
      setPlate('');
      setStateCode('');
      setText('');
      setTitle('');
      setFiles([]);
      setError('');
    } catch (submitError) {
      const message = submitError.message || 'Unable to submit review';
      if (message.includes('Too many submissions, please wait')) {
        setCooldownSeconds(60);
      }
      setError(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 sm:px-4 sm:py-8 sm:px-6">
      <div className="min-h-screen sm:min-h-0 mx-auto sm:max-w-3xl bg-white p-5 shadow-2xl sm:rounded-[32px] sm:p-10">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">New report</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Add a review with media</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">✕</button>
        </div>

        {!user && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You're posting as a guest. This is your <strong>one free review</strong> — continue with Google to post more (You remain Anonymous).
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700 sm:col-span-2">
              Title
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief title for this incident..."
                maxLength={REVIEW_TITLE_MAX_LENGTH}
                className="mt-2 w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              />
              <p className={`mt-1 text-xs ${titleCounterClass}`}>{titleCount}/{REVIEW_TITLE_MAX_LENGTH}</p>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Plate
              <input
                type="text"
                value={plate}
                onChange={handlePlateChange}
                placeholder="ABC123"
                maxLength={PLATE_MAX_LENGTH}
                className="mt-2 w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              />
              <p className={`mt-1 text-xs ${plateInputError ? 'text-rose-600' : 'text-transparent'}`}>
                {plateInputError || 'placeholder'}
              </p>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              State
              <div className="relative mt-2">
                <select
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="w-full appearance-none rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 pr-14 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                >
                  <option value="">Select a state</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute inset-y-0 right-5 flex items-center text-slate-500">
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4">
                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </label>
          </div>

          <label className="block text-sm font-medium text-slate-700">
            Description
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Describe the incident in detail..."
              rows={5}
              maxLength={REVIEW_TEXT_MAX_LENGTH}
              className="mt-2 w-full rounded-[26px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200 resize-none"
            />
            <p className={`mt-1 text-xs ${textCounterClass}`}>{textCount}/{REVIEW_TEXT_MAX_LENGTH}</p>
          </label>

          <MediaUploader files={files} onFilesChange={setFiles} />

          {submitting && uploadProgress > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                <span>Uploading media</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-slate-900 transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              {error}
            </div>
          )}

          {cooldownSeconds > 0 && (
            <div className="rounded-3xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Too many submissions, please wait {cooldownSeconds}s
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="submit"
              disabled={submitting || cooldownSeconds > 0}
              className="inline-flex h-12 w-full items-center justify-center rounded-3xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-lg shadow-slate-200/50 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 sm:h-auto sm:w-auto sm:py-3"
            >
              {submitting ? 'Uploading...' : 'Submit review'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-slate-600 transition hover:text-slate-900 text-center py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReviewModal;
